import { supabase } from '../lib/supabase'
import type { Deliverable } from '../types/portal'
import type { WriteResult } from './projectWrites'

/**
 * WHAT IS ON A CLIENT PAGE — 14 Oct 2026.
 *
 * Until now nothing anywhere declared what a client page contained. Every
 * block decided for itself, from a different table, by a different rule — six
 * conditions across five tables. The page was emergent rather than designed,
 * which is why Marjan's stopped dead after the tracker and why Tom could not
 * tell what a client would see without opening the link.
 *
 * Tom's shape: *"it should be like a menu. Tick all the things that you think
 * you want, and then that goes into more detail."*
 *
 * **A tick is a promise, not content.** Ticking `plan` does not mean a plan
 * exists; it means this page will have one. A ticked block with nothing in it
 * simply draws nothing, so a tick can never contradict the data.
 *
 * **Nothing measures progress.** There is no "partly there". Tom: *"it's
 * going to be in a state of evolving, which is the same for everything."* A
 * tracker that grows from five steps to twenty-five is not half-done at five.
 *
 * **`page_blocks` is null for every project that predates this.** Null means
 * "work it out from the data, exactly as the page always has", so no live
 * client page changed the day this shipped. The first tick writes the
 * derived set plus the change, so nothing a client can already see can
 * disappear by accident.
 *
 * The words that fill two of these blocks live in `utils/clientWords.ts` —
 * the note and the outline are content, this file is only about which blocks
 * exist.
 */

export type BlockKey =
  | 'note'
  | 'outline'
  | 'films'
  | 'photographs'
  | 'transcripts'
  | 'downloads'
  | 'dates'
  | 'plan'
  | 'report'

export interface BlockSpec {
  key: BlockKey
  /** What the producer ticks. */
  label: string
  /** One line, so nobody has to be told what it is. */
  note: string
  /**
   * Which of the two client pages it belongs to once the split lands. The
   * seam is things they act on against things they consult.
   */
  page: 'work' | 'plan'
  /** False for the blocks that have nothing to fill in — they just appear. */
  hasEditor: boolean
}

/** In page order, so the panel reads the way the client page reads. */
export const BLOCKS: BlockSpec[] = [
  {
    key: 'note',
    label: 'A note from you',
    note: 'A line at the top, in your words',
    page: 'work',
    hasEditor: true,
  },
  {
    key: 'films',
    label: 'Films to watch and approve',
    note: 'They play it, then approve or ask for a change',
    page: 'work',
    hasEditor: true,
  },
  {
    key: 'photographs',
    label: 'Photographs',
    note: 'A gallery rather than a player',
    page: 'work',
    hasEditor: true,
  },
  {
    key: 'transcripts',
    label: 'Transcripts',
    note: 'Timecoded, beside the film they belong to',
    page: 'work',
    hasEditor: false,
  },
  {
    key: 'downloads',
    label: 'Downloads',
    note: 'The finished files, to keep',
    page: 'work',
    hasEditor: false,
  },
  {
    key: 'outline',
    label: "What we're making",
    note: 'The approach, written once — worth having before anything is filmed',
    page: 'plan',
    hasEditor: true,
  },
  {
    key: 'plan',
    label: 'The plan',
    note: 'Every step, who has it, where it stands',
    page: 'plan',
    hasEditor: true,
  },
  {
    key: 'dates',
    label: 'Shoot dates',
    note: 'What is coming up, and when',
    page: 'plan',
    hasEditor: true,
  },
  {
    key: 'report',
    label: 'The wrap-up report',
    note: 'At the end: what was made, and what is next',
    page: 'work',
    hasEditor: true,
  },
]

export const BLOCK_KEYS: BlockKey[] = BLOCKS.map((block) => block.key)

const isKey = (value: string): value is BlockKey => (BLOCK_KEYS as string[]).includes(value)

/**
 * What a project that has never been ticked is already showing, worked out
 * from its own data the way the page itself works it out. This is what the
 * panel displays on a legacy project, and what gets written the first time
 * somebody ticks anything — so the first tick adds, and never silently takes
 * a block away from a client who can already see it.
 */
export function derivedBlocks(
  deliverables: Deliverable[],
  stepCount: number,
  shootCount: number,
  complete: boolean,
): BlockKey[] {
  const visible = deliverables.filter((item) => item.visibleToClient !== false)
  const on: BlockKey[] = []

  if (visible.some((item) => item.type !== 'Photography' && item.type !== 'Transcript')) {
    on.push('films')
  }
  if (visible.some((item) => item.type === 'Photography')) on.push('photographs')
  if (visible.some((item) => item.type === 'Transcript')) on.push('transcripts')
  if (visible.length > 0) on.push('downloads')
  if (stepCount > 0) on.push('plan')
  if (shootCount > 0) on.push('dates')
  if (complete) on.push('report')

  /* `note` and `outline` are new on 14 Oct — no project can already have one,
     so neither is ever derived on. They only arrive by being ticked. */
  return BLOCK_KEYS.filter((key) => on.includes(key))
}

/** The stored ticks, or null for a project nobody has ticked yet. */
export async function fetchPageBlocks(projectId: string): Promise<BlockKey[] | null> {
  if (!supabase || !projectId) return null

  const { data, error } = await supabase
    .from('projects')
    .select('page_blocks')
    .eq('id', projectId)
    .maybeSingle()

  if (error) {
    console.warn(`[supabase] page blocks load failed: ${error.message}`)
    return null
  }
  const raw = (data?.page_blocks as string[] | null | undefined) ?? null
  if (!raw) return null
  return raw.filter(isKey)
}

/** Written in page order, so the column reads the way the page does. */
export async function savePageBlocks(projectId: string, keys: BlockKey[]): Promise<WriteResult> {
  if (!supabase) return { ok: false, error: 'Supabase is not configured.' }
  const ordered = BLOCK_KEYS.filter((key) => keys.includes(key))

  const { error } = await supabase
    .from('projects')
    .update({ page_blocks: ordered, updated_at: new Date().toISOString() })
    .eq('id', projectId)

  if (error) {
    const detail = `page_blocks: ${error.message}${error.code ? ` (${error.code})` : ''}`
    console.error(`[supabase] write refused — ${detail}`)
    return { ok: false, error: detail }
  }
  return { ok: true }
}
